import { useListLocations } from '@trufla-technology/trufla-trudomain/dist/lib/truMarket';
import { ListLocationsResponse } from '@trufla-technology/trufla-trudomain/dist/lib/truMarket/types';
import {
  SelectWithFilter,
  Option,
  Loading,
} from '@trufla-technology/trumarket-react-design-system';
import { userSelectList } from 'app/types/User';
import { debounce } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';

type Location = Extract<ListLocationsResponse['data'], object>[number];

export const LocationSelect = ({
  value,
  onChange,
}: userSelectList<string[]>) => {
  const handleChange = (v: string[]) => {
    onChange?.(v);
  };
  const [locations, setLocations] = useState<ListLocationsResponse['data']>([]);
  const { data, loading, handleFilter, paging, handleOffset, query } =
    useListLocations({
      select: ['key', 'name'],
      paginate: { limit: 10, offset: 0 },
    });
  const initialRender = useRef({ loadMore: true });

  useEffect(() => {
    if (!data || data.length < 1) {
      return;
    }
    setLocations([...locations!, ...data]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const loadMore = useCallback(() => {
    if (initialRender.current.loadMore)
      return (initialRender.current.loadMore = false);
    if (locations?.length === paging?.count) return;
    return setTimeout(() => {
      handleOffset(query.paginate?.limit! + query.paginate?.offset!);
    }, 200);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, locations]);

  const handleSearch = debounce((value: string) => {
    setLocations([]);
    if (!value) {
      handleFilter({ name: undefined }, { override: true });
      return;
    }
    const searchValue = value?.trim()?.toLowerCase();
    handleFilter({ name: searchValue });
  }, 500);

  const handleScroll = (event: React.UIEvent<HTMLDivElement, UIEvent>) => {
    const { scrollTop, clientHeight, scrollHeight } =
      event.target as HTMLElement;
    const threshold = 1;
    const isBottom =
      Math.abs(scrollTop - (scrollHeight - clientHeight)) < threshold;

    if (loading || !isBottom) return;
    loadMore();
  };

  return (
    <SelectWithFilter
      value={value || []}
      mode="multiple"
      onChange={handleChange}
      onSearch={handleSearch}
      onPopupScroll={handleScroll}
      virtual={false}
    >
      {loading ? (
        <Option value="" disabled>
          <Loading size="small" />
        </Option>
      ) : locations?.length ? (
        locations?.map(({ key, name }: Location) => (
          <Option key={`select-option-${key}`} value={key} title={name}>
            <span>{name}</span>
          </Option>
        ))
      ) : (
        <Option value="" disabled>
          <span>No Data Found</span>
        </Option>
      )}
    </SelectWithFilter>
  );
};
